import * as React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Ban, Pencil, RotateCcw, Save, X, Zap } from 'lucide-react';
import { PageHeader } from '@/components/app/PageHeader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { LoadingState } from '@/components/app/LoadingState';
import { ErrorState } from '@/components/app/ErrorState';
import { StatCard } from '@/components/app/StatCard';
import { TimeseriesChart } from '@/components/app/TimeseriesChart';
import { EmptyState } from '@/components/app/EmptyState';
import { CodeBlock } from '@/components/app/CodeBlock';
import { Separator } from '@/components/ui/separator';
import { humanizeError } from '@/lib/apiClient';
import { humanizeCount, humanizePercent } from '@/lib/utils';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
import {
  useDecisionsTimeseries,
  useJourneys,
  useKillJourney,
  useRecentDecisions,
  useReviveJourney,
  useUpdateJourneyContent,
} from '../hooks';
import { JourneyStatusBadge } from '../components/JourneyStatusBadge';
import { JourneyTimeline } from '../components/JourneyTimeline';
import { describeTrigger } from '../helpers/describeTrigger';
import { outcomeBadgeVariant } from '../decisionOutcome';
import type { JourneyDefinition, JourneyStep, JourneyStepVariant } from '../types';
import type { DecisionsTimeseriesPayload } from '../api/notificationsApi';
import type { RecentDecision } from '../api/notificationsApi';
import type {
  UpdateJourneyContentBody,
  UpdateJourneyStepPatch,
  UpdateJourneyVariantPatch,
} from '../api/notificationsApi';

interface VariantDraft {
  variantId: string;
  title: string;
  body: string;
  targetRoute: string | null;
}

interface StepDraft {
  stepNumber: number;
  variants: VariantDraft[];
}

interface JourneyDraft {
  name: string;
  description: string;
  steps: StepDraft[];
}

function toDraft(journey: JourneyDefinition): JourneyDraft {
  return {
    name: journey.name,
    description: journey.description,
    steps: journey.steps.map((s) => ({
      stepNumber: s.stepNumber,
      variants: s.variants.map((v) => ({
        variantId: v.variantId,
        title: v.title,
        body: v.body,
        targetRoute: v.targetRoute ?? null,
      })),
    })),
  };
}

function buildPatch(
  original: JourneyDefinition,
  draft: JourneyDraft,
): UpdateJourneyContentBody | null {
  const body: UpdateJourneyContentBody = {};
  if (draft.name !== original.name) body.name = draft.name;
  if (draft.description !== original.description) body.description = draft.description;

  const stepPatches: UpdateJourneyStepPatch[] = [];
  for (const draftStep of draft.steps) {
    const origStep = original.steps.find((s) => s.stepNumber === draftStep.stepNumber);
    if (!origStep) continue;

    const variantPatches: UpdateJourneyVariantPatch[] = [];
    for (const dv of draftStep.variants) {
      const ov = origStep.variants.find((v) => v.variantId === dv.variantId);
      if (!ov) continue;
      const patch: UpdateJourneyVariantPatch = { variantId: dv.variantId };
      let changed = false;
      if (dv.title !== ov.title) {
        patch.title = dv.title;
        changed = true;
      }
      if (dv.body !== ov.body) {
        patch.body = dv.body;
        changed = true;
      }
      const origRoute = ov.targetRoute ?? null;
      if (dv.targetRoute !== origRoute) {
        patch.targetRoute = dv.targetRoute;
        changed = true;
      }
      if (changed) variantPatches.push(patch);
    }
    if (variantPatches.length > 0)
      stepPatches.push({ stepNumber: draftStep.stepNumber, variants: variantPatches });
  }
  if (stepPatches.length > 0) body.steps = stepPatches;

  if (body.name === undefined && body.description === undefined && !body.steps) return null;
  return body;
}

export function JourneyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, isError, error, refetch } = useJourneys();
  const timeseries = useDecisionsTimeseries({ days: 14, journeyId: id });
  const decisions = useRecentDecisions(50);
  const killMut = useKillJourney();
  const reviveMut = useReviveJourney();
  const updateMut = useUpdateJourneyContent();
  const confirm = useConfirmDialog();
  const navigate = useNavigate();

  const journey = data?.journeys.find((j) => j.journeyId === id) ?? null;

  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState<JourneyDraft | null>(null);

  // Clear draft on exit. Draft is seeded only when the user clicks Edit
  // (see `onEnterEdit`) — otherwise a background refetch of the journey
  // list would clobber in-progress typing.
  React.useEffect(() => {
    if (!editing) setDraft(null);
  }, [editing]);

  const onEnterEdit = () => {
    if (!journey) return;
    setDraft(toDraft(journey));
    setEditing(true);
  };

  if (isLoading) return <LoadingState label="Loading journey" />;
  if (isError)
    return (
      <ErrorState
        title="Journey unavailable"
        description={humanizeError(error)}
        actionLabel="Retry"
        onAction={() => refetch()}
      />
    );
  if (!journey)
    return (
      <ErrorState
        title="Journey not found"
        description={`No journey with id "${id}".`}
        actionLabel="Back to journeys"
        actionHref="/notifications/journeys"
      />
    );

  const patch = editing && draft ? buildPatch(journey, draft) : null;
  const canSave = editing && patch !== null && !updateMut.isPending;

  const onKill = () =>
    confirm({
      title: `Kill "${journey.name}"?`,
      typedConfirmation: journey.journeyId,
      destructive: true,
      confirmLabel: 'Kill journey',
      onConfirm: async ({ reason }) => {
        await killMut.mutateAsync({ journeyId: journey.journeyId, reason });
      },
    });

  const onRevive = () =>
    confirm({
      title: `Revive "${journey.name}"?`,
      reasonRequired: false,
      onConfirm: async ({ reason }) => {
        await reviveMut.mutateAsync({ journeyId: journey.journeyId, reason: reason || undefined });
      },
    });

  const onSave = async () => {
    if (!patch) return;
    await updateMut.mutateAsync({ journeyId: journey.journeyId, body: patch });
    setEditing(false);
  };

  return (
    <div className="space-y-6">
      <Button variant="ghost" size="sm" onClick={() => navigate('/notifications/journeys')}>
        <ArrowLeft className="h-4 w-4" /> All journeys
      </Button>

      <PageHeader
        title={editing ? `Editing: ${journey.name}` : journey.name}
        description={editing ? 'Changes apply immediately after Save.' : journey.description}
        actions={
          <div className="flex items-center gap-2">
            <JourneyStatusBadge journey={journey} />
            {editing ? (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditing(false)}
                  disabled={updateMut.isPending}
                >
                  <X className="h-4 w-4" /> Cancel
                </Button>
                <Button size="sm" onClick={onSave} disabled={!canSave}>
                  <Save className="h-4 w-4" />
                  {updateMut.isPending ? 'Saving…' : 'Save changes'}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" size="sm" onClick={onEnterEdit}>
                  <Pencil className="h-4 w-4" /> Edit
                </Button>
                {journey.isActive ? (
                  <Button variant="destructive" size="sm" onClick={onKill}>
                    <Ban className="h-4 w-4" /> Kill
                  </Button>
                ) : (
                  <Button variant="outline" size="sm" onClick={onRevive}>
                    <RotateCcw className="h-4 w-4" /> Revive
                  </Button>
                )}
              </>
            )}
          </div>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>Definition</CardTitle>
          <CardDescription>
            <span className="font-mono">{journey.journeyId}</span> · version {journey.version} · updated {journey.updatedAt}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline">Trigger: {journey.entryTrigger.type}</Badge>
            <Badge variant="outline">
              Pause after {journey.pauseAfterConsecutiveIgnores} ignores · {journey.pauseDurationHours}h
            </Badge>
            <Badge variant="outline">{journey.steps.length} steps</Badge>
            <Badge variant="outline">{journey.stopConditions.length} stop conditions</Badge>
          </div>
          <p className="text-xs text-muted-foreground">{describeTrigger(journey.entryTrigger)}</p>
          {editing && draft ? (
            <div className="space-y-3 pt-2">
              <div className="space-y-1.5">
                <Label htmlFor="journey-name">Name</Label>
                <Input
                  id="journey-name"
                  value={draft.name}
                  maxLength={100}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="journey-desc">Description</Label>
                <Textarea
                  id="journey-desc"
                  value={draft.description}
                  maxLength={500}
                  rows={2}
                  onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                />
              </div>
            </div>
          ) : null}
          {journey.killReason && !editing && (
            <div className="rounded-md border-l-4 border-destructive bg-destructive/5 p-3 text-xs">
              <div className="font-semibold text-destructive">Killed</div>
              <div>by {journey.killedBy || 'unknown'} at {journey.killedAt || '?'}</div>
              <div className="mt-1 text-muted-foreground">"{journey.killReason}"</div>
            </div>
          )}
        </CardContent>
      </Card>

      <InsightsSection
        journey={journey}
        timeseries={timeseries.data?.timeseries}
        timeseriesLoading={timeseries.isLoading}
        decisions={decisions.data?.decisions ?? []}
        decisionsLoading={decisions.isLoading}
      />

      <Card>
        <CardHeader>
          <CardTitle>Steps</CardTitle>
          {editing && (
            <CardDescription>
              Edit variant title, body, and deep-link. Step metadata (timing, category, channel,
              priority) stays read-only — those live in code.
            </CardDescription>
          )}
        </CardHeader>
        <CardContent>
          <ol className="space-y-3">
            {journey.steps.map((step) => (
              <StepCard
                key={step.stepNumber}
                step={step}
                draft={
                  editing && draft
                    ? draft.steps.find((s) => s.stepNumber === step.stepNumber) ?? null
                    : null
                }
                onVariantPatch={
                  editing && draft
                    ? (variantId, variantPatch) => {
                        setDraft({
                          ...draft,
                          steps: draft.steps.map((s) =>
                            s.stepNumber !== step.stepNumber
                              ? s
                              : {
                                  ...s,
                                  variants: s.variants.map((v) =>
                                    v.variantId !== variantId ? v : { ...v, ...variantPatch },
                                  ),
                                },
                          ),
                        });
                      }
                    : null
                }
              />
            ))}
          </ol>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Stop conditions</CardTitle>
        </CardHeader>
        <CardContent>
          <CodeBlock value={journey.stopConditions} />
        </CardContent>
      </Card>
    </div>
  );
}

type VariantPatcher = (variantId: string, patch: Partial<VariantDraft>) => void;

function StepCard({
  step,
  draft,
  onVariantPatch,
}: {
  step: JourneyStep;
  draft: StepDraft | null;
  onVariantPatch: VariantPatcher | null;
}) {
  return (
    <li className="rounded-md border p-3">
      <div className="flex items-center gap-2 text-sm">
        <Badge>{`Step ${step.stepNumber}`}</Badge>
        <Badge variant="outline">{step.category}</Badge>
        <Badge variant="outline">{step.channel}</Badge>
        <Badge variant="outline">{step.priority}</Badge>
        {step.isUrgent && <Badge variant="warning">Urgent</Badge>}
        {step.isEscalation && <Badge variant="destructive">Escalation</Badge>}
        <span className="ml-auto text-xs text-muted-foreground">+{step.delayMinutes}m</span>
      </div>
      <Separator className="my-2" />
      <div className="text-xs">
        <div className="font-mono text-muted-foreground">{step.notificationType}</div>
        <ul className="mt-2 space-y-2">
          {step.variants.map((variant) => (
            <VariantCard
              key={variant.variantId}
              variant={variant}
              draft={draft?.variants.find((v) => v.variantId === variant.variantId) ?? null}
              onPatch={onVariantPatch}
            />
          ))}
        </ul>
      </div>
    </li>
  );
}

function VariantCard({
  variant,
  draft,
  onPatch,
}: {
  variant: JourneyStepVariant;
  draft: VariantDraft | null;
  onPatch: VariantPatcher | null;
}) {
  if (!draft || !onPatch) {
    return (
      <li className="rounded border bg-muted/30 p-2">
        <div className="font-semibold">{variant.title}</div>
        <div className="text-muted-foreground">{variant.body}</div>
        {variant.targetRoute && (
          <div className="mt-1 text-[10px] uppercase tracking-wide text-muted-foreground">
            → <code className="font-mono">{variant.targetRoute}</code>
          </div>
        )}
      </li>
    );
  }

  return (
    <li className="rounded border bg-muted/30 p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <code className="text-[10px] uppercase tracking-wide text-muted-foreground">
          {variant.variantId}
        </code>
      </div>
      <div className="space-y-1">
        <Label htmlFor={`title-${variant.variantId}`} className="text-[10px] uppercase">
          Title
        </Label>
        <Input
          id={`title-${variant.variantId}`}
          value={draft.title}
          maxLength={200}
          onChange={(e) => onPatch(variant.variantId, { title: e.target.value })}
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor={`body-${variant.variantId}`} className="text-[10px] uppercase">
          Body
        </Label>
        <Textarea
          id={`body-${variant.variantId}`}
          value={draft.body}
          maxLength={2000}
          rows={4}
          onChange={(e) => onPatch(variant.variantId, { body: e.target.value })}
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor={`route-${variant.variantId}`} className="text-[10px] uppercase">
          Target route
        </Label>
        <div className="flex gap-2">
          <Input
            id={`route-${variant.variantId}`}
            value={draft.targetRoute ?? ''}
            maxLength={200}
            placeholder="(none)"
            onChange={(e) =>
              onPatch(variant.variantId, { targetRoute: e.target.value || null })
            }
          />
          {draft.targetRoute !== null && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => onPatch(variant.variantId, { targetRoute: null })}
            >
              Clear
            </Button>
          )}
        </div>
      </div>
    </li>
  );
}

// ── Insights section ────────────────────────────────────────────

interface JourneyAggregate {
  totalSends: number;
  totalOpens: number;
  openRate: number | null;
  sendsByDay: Array<{ ts: string; value: number }>;
  opensByDay: Array<{ ts: string; value: number }>;
  skipReasons: Array<{ reason: string; count: number }>;
}

function aggregateFromTimeseries(
  payload: DecisionsTimeseriesPayload | undefined,
): JourneyAggregate {
  const sendsByDay: Array<{ ts: string; value: number }> = [];
  const opensByDay: Array<{ ts: string; value: number }> = [];

  for (const d of payload?.days ?? []) {
    sendsByDay.push({ ts: d.day, value: d.sends });
    opensByDay.push({ ts: d.day, value: d.opens });
  }

  const totalSends = payload?.totals.sends ?? 0;
  const totalOpens = payload?.totals.opens ?? 0;
  const openRate = totalSends > 0 ? totalOpens / totalSends : null;
  const skipReasons = (payload?.skipReasons ?? []).slice(0, 6);

  return { totalSends, totalOpens, openRate, sendsByDay, opensByDay, skipReasons };
}


function formatRelativeTs(iso?: string): string {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return iso;
  if (ms < 60_000) return 'just now';
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.round(ms / 3_600_000)}h ago`;
  return `${Math.round(ms / 86_400_000)}d ago`;
}

function InsightsSection({
  journey,
  timeseries,
  timeseriesLoading,
  decisions,
  decisionsLoading,
}: {
  journey: JourneyDefinition;
  timeseries: DecisionsTimeseriesPayload | undefined;
  timeseriesLoading: boolean;
  decisions: RecentDecision[];
  decisionsLoading: boolean;
}) {
  const agg = React.useMemo(() => aggregateFromTimeseries(timeseries), [timeseries]);

  const filteredDecisions = React.useMemo(
    () => decisions.filter((d) => d.journeyId === journey.journeyId),
    [decisions, journey.journeyId],
  );
  const scopedDecisions = React.useMemo(
    () => filteredDecisions.slice(0, 12),
    [filteredDecisions],
  );

  const maxSkip = agg.skipReasons[0]?.count ?? 0;

  const trendSeries = React.useMemo(
    () => [
      { key: 'sends', label: 'Sends', color: '#4f46e5', values: agg.sendsByDay },
      { key: 'opens', label: 'Opens', color: '#ff3366', values: agg.opensByDay },
    ],
    [agg.sendsByDay, agg.opensByDay],
  );

  const loadingPlaceholder = timeseriesLoading && !timeseries;
  const displaySends = loadingPlaceholder ? '…' : humanizeCount(agg.totalSends);
  const displayOpensHint = loadingPlaceholder
    ? undefined
    : `${humanizeCount(agg.totalOpens)} opens`;
  const displayOpenRate = loadingPlaceholder
    ? '…'
    : agg.openRate === null
      ? '—'
      : humanizePercent(agg.openRate);
  const openRateTone: 'default' | 'success' | 'warning' | 'destructive' = loadingPlaceholder
    ? 'default'
    : agg.openRate === null
      ? 'default'
      : agg.openRate >= 0.3
        ? 'success'
        : agg.openRate >= 0.1
          ? 'warning'
          : 'destructive';

  const lastSendDecision = filteredDecisions.find((d) => d.outcome === 'send');
  const lastDecisionAny = filteredDecisions[0];
  const lastFiredCaption = (() => {
    if (!lastDecisionAny) return 'Never fired in the recent decisions window.';
    const lastSendTxt = lastSendDecision
      ? `Last send: ${formatRelativeTs(lastSendDecision.decidedAt)}`
      : 'No send in recent window';
    const lastDecTxt = `last decision: ${formatRelativeTs(lastDecisionAny.decidedAt)}`;
    return `${lastSendTxt} · ${lastDecTxt}`;
  })();

  return (
    <section className="space-y-4">
      <p className="text-xs text-muted-foreground">{lastFiredCaption}</p>
      <section className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <StatCard
          label="Sends · 14d"
          value={displaySends}
          hint={displayOpensHint}
          icon={Zap}
        />
        <StatCard
          label="Open rate · 14d"
          value={displayOpenRate}
          hint={loadingPlaceholder || agg.totalSends > 0 ? undefined : 'no sends yet'}
          tone={openRateTone}
        />
        <StatCard
          label="Steps"
          value={journey.steps.length}
          hint={`pause after ${journey.pauseAfterConsecutiveIgnores} ignores`}
        />
      </section>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Timeline</CardTitle>
          <CardDescription>
            Cumulative delay from entry. Tells you when each step actually fires.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <JourneyTimeline steps={journey.steps} />
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">14-day trend</CardTitle>
            <CardDescription>Daily sends vs opens for this journey.</CardDescription>
          </CardHeader>
          <CardContent>
            {timeseriesLoading ? (
              <LoadingState inline label="Loading trend" />
            ) : agg.totalSends === 0 ? (
              <EmptyState title="No sends in the last 14 days" />
            ) : (
              <TimeseriesChart
                series={trendSeries}
                height={200}
                yFormatter={(v) => humanizeCount(v)}
              />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Top skip reasons</CardTitle>
            <CardDescription>
              Why Decision Brain held back for this journey (14d window).
            </CardDescription>
          </CardHeader>
          <CardContent>
            {timeseriesLoading ? (
              <LoadingState inline />
            ) : agg.skipReasons.length === 0 ? (
              <EmptyState title="No skips in window" description="Everything eligible was sent." />
            ) : (
              <ul className="space-y-2">
                {agg.skipReasons.map(({ reason, count }) => {
                  const pct = maxSkip > 0 ? (count / maxSkip) * 100 : 0;
                  return (
                    <li key={reason} className="space-y-1">
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-mono">{reason}</span>
                        <span className="text-muted-foreground">{humanizeCount(count)}</span>
                      </div>
                      <div
                        aria-hidden
                        className="h-1.5 overflow-hidden rounded-full bg-muted"
                      >
                        <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Recent decisions for this journey</CardTitle>
          <CardDescription>
            Last 12 decisions tagged with this journey, auto-refetch every 10s.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <JourneyIdleBanner decisions={scopedDecisions} />
          {decisionsLoading && scopedDecisions.length === 0 ? (
            <LoadingState inline />
          ) : scopedDecisions.length === 0 ? (
            <p className="px-4 py-6 text-center text-xs text-muted-foreground">
              No decisions logged for this journey in the recent window.
            </p>
          ) : (
            <ul className="divide-y text-sm">
              {scopedDecisions.map((d) => (
                <li
                  key={d.decisionId}
                  className="grid grid-cols-[auto_1fr_auto] items-center gap-3 px-4 py-2 hover:bg-muted/40"
                >
                  <Badge variant={outcomeBadgeVariant(d.outcome)}>{d.outcome}</Badge>
                  <div className="min-w-0">
                    <Link
                      to={`/notifications/decisions/${encodeURIComponent(d.decisionId)}`}
                      className="block min-w-0 hover:underline"
                    >
                      <div className="truncate text-xs">
                        <span className="font-medium">{d.notificationType}</span>
                        {d.category && (
                          <span className="text-muted-foreground"> · {d.category}</span>
                        )}
                      </div>
                    </Link>
                    <div className="truncate text-[11px] text-muted-foreground">
                      <Link
                        to={`/notifications/users/${encodeURIComponent(d.userId)}`}
                        className="font-mono hover:underline"
                      >
                        {d.userId.slice(0, 8)}…
                      </Link>
                      {d.reasonCodes && d.reasonCodes.length > 0 && (
                        <> · {d.reasonCodes.join(', ')}</>
                      )}
                    </div>
                  </div>
                  <div className="text-right text-[11px] text-muted-foreground">
                    {formatRelativeTs(d.decidedAt)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </section>
  );
}

const JOURNEY_IDLE_THRESHOLD_MS = 30 * 60_000;

function JourneyIdleBanner({ decisions }: { decisions: RecentDecision[] }) {
  const newest = decisions[0];
  if (!newest) return null;
  const ageMs = Date.now() - new Date(newest.decidedAt).getTime();
  if (!Number.isFinite(ageMs) || ageMs < JOURNEY_IDLE_THRESHOLD_MS) return null;
  const idleMin = Math.round(ageMs / 60_000);
  return (
    <div className="mx-4 mt-4 rounded-md border border-dashed bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
      This journey has been quiet — last decision {idleMin}m ago.
    </div>
  );
}
